import { useState, useMemo, useEffect, useRef, useContext, useCallback, Fragment, type ReactNode } from 'react';
import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Icon,
    Tag,
    TagLabel,
    Skeleton,
    Link,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
    useToast,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverBody,
    Checkbox,
    Wrap,
    WrapItem,
    Tooltip,
    Divider,
} from '@chakra-ui/react';
import {
    TbSearch,
    TbBookmark,
    TbFlag,
    TbDotsVertical,
    TbAdjustmentsHorizontal,
    TbShieldCheck,
    TbCar,
    TbBuilding,
    TbTruck,
    TbMaximize,
    TbMinimize,
    TbReceipt,
    TbPuzzle,
    TbDownload,
} from 'react-icons/tb';
import {
    MenuSectionLabel,
    MenuFooterActions,
    checkboxMenuPopoverContentSx,
} from '../../../ui';
import { Link as RouterLink } from 'react-router-dom';
import { useAuditingOrders, useAuditingParts } from '../../../hooks/reporting';
import { useReportingFilters } from '../ReportingFiltersContext';
import ExcludeEvsToggle from '../components/ExcludeEvsToggle';
import { fetchAllAuditingOrders } from '../../../hooks/reporting/useAuditingOrders';
import { fetchAllAuditingParts } from '../../../hooks/reporting/useAuditingParts';
import SessionContext from '../../../contexts/SessionContext';
import { hasInventoryReporting } from '../../../utils/inventoryReportingAccess';
import {
    INVOICING_GATED_AUDIT_ORDER_KEYS,
    INVOICING_GATED_AUDIT_PART_KEYS,
    filterKeysForInvoicing,
    orgShowsInvoicingReportingMetrics,
} from '../shared/invoicingGatedMetrics';
import { useAuditingBookmarks, useCreateAuditingBookmark, useDeleteAuditingBookmark, getBookmarkTypes } from '../../../hooks/reporting/useAuditingBookmarks';
import Pagination from '../../../components/Pagination';
import PartType from '../../../components/PartType';
import ResizableTh from '../../../components/ResizableTh';
import { useColumnWidths } from '../../../hooks/useColumnWidths';
import SourceBadge from '../../../components/SourceBadge';
import LocationSelector from '../../../components/LocationSelector';
import PartOptionsModal from '../../../modals/PartOptionsModal';
import type { AuditingOrderItem, AuditingPartItem, AuditingBookmarkTargetType, AuditingBookmarkType } from '../../../types/reporting';
import { useTableSettings } from '../../../hooks/useTableSettings';
import { useReportingColumnPrefs } from '../../../hooks/useReportingColumnPrefs';
import { getPartTypeLabel } from '../../../utils/chartUtils';
import { exportToCsv } from '../../../utils/csvExport';
import ReportingFullScreenShell from '../components/ReportingFullScreenShell';
import ReportingTableSettingsDrawer from '../components/ReportingTableSettingsDrawer';
import {
    readAuditingTableSource,
    writeAuditingTableSource,
    useAuditingInventory,
    type AuditingTableSource,
} from '../components/useAuditingInventory';
import { columnDrawerRowsToPrefs, type SettingsColumnDrawerRow } from '../components/SettingsColumnsPanel';
import DatePicker from '../../../components/DatePicker';
import type { AuditingDrilldownPayload } from '../widgets/WidgetRenderer';
import SortArrowIcon, { type SortIconDirection } from '../../../components/SortArrowIcon';
import {
    AUDIT_ORDER_COLUMN_KEYS,
    AUDIT_ORDER_DEFAULT_HIDDEN,
    AUDIT_ORDER_LABELS,
    AUDIT_ORDER_PINNED_KEYS,
    AUDIT_PART_COLUMN_KEYS,
    AUDIT_PART_DEFAULT_HIDDEN,
    AUDIT_PART_LABELS,
    AUDIT_PART_PINNED_KEYS,
} from '../auditingTableConfig';
import { AutomationTarget } from '../../../hooks/useGetOrderWorkflowStatus';

interface AuditingTabProps {
    startDate: string;
    endDate: string;
    locationId?: string;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    /** Backend preset slug from reporting header (e.g. last_7_days, custom). */
    preset?: string;
    /** When false (another Reporting tab is selected), fullscreen mode is closed. */
    isActive?: boolean;
    pendingDrilldown?: AuditingDrilldownPayload | null;
    onDrilldownApplied?: () => void;
}

type ViewMode = 'orders' | 'parts' | 'setAside';
type SortDirection = 'asc' | 'desc' | null;
type RangeFilter = { min: string; max: string; };

// Formatting helpers
const formatCurrency = (value: number) => {
    if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`;
    }
    if (value < 1 && value > 0) {
        return `$${value.toFixed(2)}`;
    }
    return `$${value.toFixed(0)}`;
};

const formatCurrencyPrecise = (value: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

/**
 * finalEta is location-local wall time serialized with a "Z" suffix (not true UTC).
 * Format from the YYYY-MM-DD prefix so US timezones don't shift the calendar day.
 */
const formatFinalEtaWallDate = (dateStr: string) => {
    const datePart = dateStr.slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
    if (!match) return formatDate(dateStr);
    const [, y, m, d] = match;
    const yy = y.slice(2);
    return `${m}/${d}/${yy}`;
};

const truncateText = (text: string, maxLength: number = 25) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

const getComparablePartTypeKeys = (value?: string | null) => {
    if (!value) return [];
    const raw = value.trim();
    const display = getPartTypeLabel(raw).trim();
    return Array.from(new Set([raw.toLowerCase(), display.toLowerCase()]));
};

const normalizeComparableText = (value?: string | null) =>
    value
        ? value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ')
        : '';


const normalizePartTypeSelections = (
    values: string[],
    options: Array<{ value: string; label: string; }>
) => {
    if (values.length === 0 || options.length === 0) return values;

    const canonicalByKey = new Map<string, string>();
    options.forEach((option) => {
        getComparablePartTypeKeys(option.value).forEach((key) => canonicalByKey.set(key, option.value));
        getComparablePartTypeKeys(option.label).forEach((key) => canonicalByKey.set(key, option.value));
    });

    return Array.from(
        new Set(
            values.map((value) => {
                const match = getComparablePartTypeKeys(value)
                    .map((key) => canonicalByKey.get(key))
                    .find((candidate): candidate is string => Boolean(candidate));
                return match ?? value;
            })
        )
    );
};

const normalizeVendorSelections = (
    values: string[],
    options: Array<{ value: string; label: string; }>
) => {
    if (values.length === 0 || options.length === 0) return values;

    const normalizedOptions = options.map((option) => ({
        option,
        valueKey: normalizeComparableText(option.value),
        labelKey: normalizeComparableText(option.label),
    }));

    return Array.from(
        new Set(
            values.flatMap((value) => {
                const selectionKey = normalizeComparableText(value);
                if (!selectionKey) return [];

                const exactMatches = normalizedOptions.filter(
                    ({ valueKey, labelKey }) => valueKey === selectionKey || labelKey === selectionKey
                );
                if (exactMatches.length > 0) {
                    return exactMatches.map(({ option }) => option.value);
                }

                const partialMatches = normalizedOptions.filter(
                    ({ valueKey, labelKey }) =>
                        valueKey.includes(selectionKey) ||
                        labelKey.includes(selectionKey) ||
                        selectionKey.includes(valueKey) ||
                        selectionKey.includes(labelKey)
                );
                if (partialMatches.length > 0) {
                    return partialMatches.map(({ option }) => option.value);
                }

                return [value];
            })
        )
    );
};

const auditingOrderGrossProfit = (o: AuditingOrderItem) =>
    typeof o.grossProfit === 'number' ? o.grossProfit : o.totalListPrice - o.totalBuyPrice;

const partVehicleYmmDisplay = (p: AuditingPartItem) =>
    p.vehicleYmm ||
    [p.vehicleYear, p.vehicleMake, p.vehicleModel].filter((x) => x != null && String(x).length > 0).join(' ') ||
    '—';

const writerNamesDisplay = (writerNames?: string[]) =>
    writerNames && writerNames.length > 0 ? writerNames.join(', ') : '—';


// Filter selector component for global filters in header (styled like Estimate/Flagged/Excluded)
type FilterSelectionUpdater = string[] | ((prev: string[]) => string[]);

interface FilterSelectorProps {
    label: string; // e.g., "All Insurance" - shown in popover checkbox
    placeholder: string; // e.g., "Select Insurance" - shown on button when nothing selected
    options: Array<{ value: string; label: string; }>;
    selectedValues: string[];
    onChange: (values: FilterSelectionUpdater) => void;
    width?: string;
    icon?: React.ElementType | any; // Optional icon to show on the left of the trigger button
}

const FilterSelector = ({ label, placeholder, options, selectedValues, onChange, width = '150px', icon }: FilterSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);

    useEffect(() => {
        if (isOpen && triggerRef.current) {
            setPopoverWidth(triggerRef.current.offsetWidth);
        }
    }, [isOpen]);

    // Filter by search term, then sort with selected items at the top
    const filteredOptions = options
        .filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a, b) => {
            const aSelected = selectedValues.includes(a.value);
            const bSelected = selectedValues.includes(b.value);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return 0;
        });

    /** Empty selection = no filter (show all). Full explicit selection only counts when there is more than one option — otherwise one row (e.g. one make in data) looked like "all" and hid the real choice. */
    const explicitAllSelected =
        options.length > 1 &&
        selectedValues.length === options.length &&
        options.every((o) => selectedValues.includes(o.value));

    const allOptionsSelected =
        options.length === 0 ? selectedValues.length === 0 : selectedValues.length === 0 || explicitAllSelected;

    const getDisplayLabel = () => {
        if (selectedValues.length === 0 || explicitAllSelected) {
            return label;
        }
        if (selectedValues.length === 1) {
            return (
                options.find((o) => o.value === selectedValues[0])?.label ??
                selectedValues[0] ??
                placeholder
            );
        }
        return options.find((o) => o.value === selectedValues[0])?.label ?? selectedValues[0] ?? '';
    };

    const isSelected = (value: string) => selectedValues.includes(value);

    const toggleOption = (value: string) => {
        onChange((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
    };

    const handleClear = () => {
        onChange([]);
    };

    return (
        <Popover
            placement="bottom-end"
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => { setIsOpen(false); setSearchTerm(''); }}
        >
            <PopoverTrigger>
                <Button
                    ref={triggerRef}
                    variant="outline"
                    w={width}
                    h="36px"
                    bg="white"
                    borderColor="gray.300"
                    borderRadius="md"
                    fontWeight="normal"
                    textAlign="left"
                    justifyContent="flex-start"
                    p={0}
                    _hover={{ borderColor: 'gray.400' }}
                >
                    <HStack w="100%" px={4} py={2} spacing={2}>
                        {icon && <Icon as={icon as React.ElementType} boxSize={4} color="gray.500" flexShrink={0} />}
                        <Box flex={1} minW={0}>
                            {selectedValues.length > 1 ? (
                                <HStack spacing={1} align="baseline">
                                    <Text noOfLines={1} color="gray.700" fontSize="sm">{getDisplayLabel()}</Text>
                                    <Text color="#0071E3" fontWeight="medium" whiteSpace="nowrap" fontSize="sm">
                                        +{selectedValues.length - 1} more
                                    </Text>
                                </HStack>
                            ) : (
                                <Text noOfLines={1} fontSize="sm">{getDisplayLabel()}</Text>
                            )}
                        </Box>
                    </HStack>
                </Button>
            </PopoverTrigger>
            <PopoverContent w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth} maxH="400px" overflowY="auto">
                <PopoverArrow />
                <PopoverBody p={3}>
                    <VStack align="stretch" spacing={2}>
                        {/* Select All option - checked when showing all (empty = all, or explicitly all selected) */}
                        <Checkbox
                            isChecked={allOptionsSelected}
                            onChange={() => handleClear()}
                        >
                            <Text fontWeight="semibold">{label}</Text>
                        </Checkbox>

                        {/* Selected items preview */}
                        {selectedValues.length > 0 && !explicitAllSelected && (
                            <Box bg="gray.100" p={2} borderRadius="md" maxH="80px" overflowY="auto">
                                <Wrap spacing={1}>
                                    {selectedValues.map(val => {
                                        const opt = options.find(o => o.value === val);
                                        if (!opt) return null;
                                        return (
                                            <WrapItem key={val}>
                                                <Tag size="sm" borderRadius="full" variant="solid" colorScheme="blue">
                                                    <TagLabel>{opt.label}</TagLabel>
                                                </Tag>
                                            </WrapItem>
                                        );
                                    })}
                                </Wrap>
                            </Box>
                        )}

                        <Divider borderColor="gray.300" />

                        {/* Search input */}
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3} />
                            </InputLeftElement>
                            <Input
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                bg="white"
                                borderColor="gray.300"
                            />
                        </InputGroup>

                        {/* Options */}
                        <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto">
                            {filteredOptions.map(opt => (
                                <Checkbox
                                    key={opt.value}
                                    isChecked={isSelected(opt.value)}
                                    onChange={() => toggleOption(opt.value)}
                                >
                                    {opt.label}
                                </Checkbox>
                            ))}
                            {filteredOptions.length === 0 && (
                                <Text color="gray.500" textAlign="center" py={2}>
                                    No matches found
                                </Text>
                            )}
                        </VStack>
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

// Sort dropdown with optional range filtering — styled to match AdvancedFilters
interface SortDropdownProps {
    rangeFilter?: RangeFilter;
    onRangeChange?: (range: RangeFilter | null) => void;
    rangeType?: 'number' | 'date';
}

const SortDropdown = ({ rangeFilter, onRangeChange, rangeType = 'number' }: SortDropdownProps) => {
    const [localMin, setLocalMin] = useState('');
    const [localMax, setLocalMax] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const hasRange = !!(rangeFilter && (rangeFilter.min || rangeFilter.max));

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => {
                setIsOpen(true);
                setLocalMin(rangeFilter?.min ?? '');
                setLocalMax(rangeFilter?.max ?? '');
            }}
            onClose={() => setIsOpen(false)}
            placement="bottom-end"
        >
            <PopoverTrigger>
                <IconButton
                    variant="ghost"
                    size="xs"
                    minW="auto"
                    h="18px"
                    px={1}
                    icon={<Icon as={TbAdjustmentsHorizontal as React.ElementType} boxSize={3} />}
                    color={hasRange ? '#0071E3' : 'gray.400'}
                    _hover={{ color: '#0071E3', bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    aria-label="Filter by range"
                />
            </PopoverTrigger>
            <PopoverContent w="220px" sx={checkboxMenuPopoverContentSx}>
                <PopoverBody p={0}>
                    <VStack align="stretch" spacing={0}>
                        {onRangeChange && (
                            <>
                                <MenuSectionLabel>Range</MenuSectionLabel>
                                <Box px={3} pb={2}>
                                    <VStack spacing={3} align="stretch">
                                        {rangeType === 'date' ? (
                                            <>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>START DATE</Text>
                                                    <DatePicker
                                                        value={localMin ? new Date(localMin + 'T00:00:00') : new Date()}
                                                        onChange={(d) => {
                                                            const y = d.getFullYear();
                                                            const m = String(d.getMonth() + 1).padStart(2, '0');
                                                            const day = String(d.getDate()).padStart(2, '0');
                                                            setLocalMin(`${y}-${m}-${day}`);
                                                        }}
                                                        size="sm"
                                                        fontSize="sm"
                                                    />
                                                </Box>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>END DATE</Text>
                                                    <DatePicker
                                                        value={localMax ? new Date(localMax + 'T00:00:00') : new Date()}
                                                        onChange={(d) => {
                                                            const y = d.getFullYear();
                                                            const m = String(d.getMonth() + 1).padStart(2, '0');
                                                            const day = String(d.getDate()).padStart(2, '0');
                                                            setLocalMax(`${y}-${m}-${day}`);
                                                        }}
                                                        size="sm"
                                                        fontSize="sm"
                                                    />
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>MIN</Text>
                                                    <Input
                                                        size="sm"
                                                        type="number"
                                                        placeholder="0"
                                                        value={localMin}
                                                        onChange={(e) => setLocalMin(e.target.value)}
                                                        bg="white"
                                                        borderColor="gray.200"
                                                        fontSize="sm"
                                                        _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>MAX</Text>
                                                    <Input
                                                        size="sm"
                                                        type="number"
                                                        placeholder="999"
                                                        value={localMax}
                                                        onChange={(e) => setLocalMax(e.target.value)}
                                                        bg="white"
                                                        borderColor="gray.200"
                                                        fontSize="sm"
                                                        _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </VStack>
                                </Box>
                                <MenuFooterActions
                                    onSecondary={() => {
                                        setLocalMin('');
                                        setLocalMax('');
                                        onRangeChange(null);
                                    }}
                                    onApply={() => {
                                        if (!localMin && !localMax) {
                                            onRangeChange(null);
                                        } else {
                                            onRangeChange({ min: localMin, max: localMax });
                                        }
                                        setIsOpen(false);
                                    }}
                                />
                            </>
                        )}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

const AuditingTab = ({
    startDate,
    endDate,
    preset,
    locationId,
    isActive = true,
    pendingDrilldown,
    onDrilldownApplied,
}: AuditingTabProps) => {
    const { excludeEvs } = useReportingFilters();
    const sessionContext = useContext(SessionContext);
    const invoicingEnabled = orgShowsInvoicingReportingMetrics(sessionContext);
    const inventoryAvailable = hasInventoryReporting(sessionContext);
    const [tableSource, setTableSource] = useState<AuditingTableSource>(() =>
        readAuditingTableSource(inventoryAvailable),
    );

    useEffect(() => {
        if (!inventoryAvailable && tableSource === 'inventory') {
            setTableSource('procurement');
            writeAuditingTableSource('procurement');
        }
    }, [inventoryAvailable, tableSource]);

    const handleTableSourceChange = useCallback((next: AuditingTableSource) => {
        setTableSource(next);
        writeAuditingTableSource(next);
    }, []);

    const inventory = useAuditingInventory({
        startDate,
        endDate,
        locationId,
        preset,
        isActive: isActive && tableSource === 'inventory' && inventoryAvailable,
    });

    // View mode state
    const [viewMode, setViewMode] = useState<ViewMode>('orders');
    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        if (!isActive) setIsFullScreen(false);
    }, [isActive]);
    const { settings: auditTableSettings, updateSettings: updateAuditTableSettings, tableSettingsSx } =
        useTableSettings('reporting_audit');
    const tableDetailMode = auditTableSettings.density === 'detailed';

    const orderColumnKeys = useMemo(
        () => filterKeysForInvoicing(AUDIT_ORDER_COLUMN_KEYS, INVOICING_GATED_AUDIT_ORDER_KEYS, invoicingEnabled),
        [invoicingEnabled],
    );
    const orderDefaultHidden = useMemo(
        () => filterKeysForInvoicing(AUDIT_ORDER_DEFAULT_HIDDEN, INVOICING_GATED_AUDIT_ORDER_KEYS, invoicingEnabled),
        [invoicingEnabled],
    );
    const partColumnKeys = useMemo(
        () => filterKeysForInvoicing(AUDIT_PART_COLUMN_KEYS, INVOICING_GATED_AUDIT_PART_KEYS, invoicingEnabled),
        [invoicingEnabled],
    );
    const partDefaultHidden = useMemo(
        () => filterKeysForInvoicing(AUDIT_PART_DEFAULT_HIDDEN, INVOICING_GATED_AUDIT_PART_KEYS, invoicingEnabled),
        [invoicingEnabled],
    );

    const ordersColumnPrefs = useReportingColumnPrefs(
        'reporting_audit_orders_cols',
        orderColumnKeys,
        AUDIT_ORDER_PINNED_KEYS,
        orderDefaultHidden
    );
    const partsColumnPrefs = useReportingColumnPrefs(
        'reporting_audit_parts_cols',
        partColumnKeys,
        AUDIT_PART_PINNED_KEYS,
        partDefaultHidden
    );

    const orderColumnDrawerRows = useMemo(
        () =>
            ordersColumnPrefs.columnConfigForDrawer.map((r) => ({
                ...r,
                label: AUDIT_ORDER_LABELS[r.key] ?? r.key,
            })),
        [ordersColumnPrefs.columnConfigForDrawer]
    );
    const partColumnDrawerRows = useMemo(
        () =>
            partsColumnPrefs.columnConfigForDrawer.map((r) => ({
                ...r,
                label: AUDIT_PART_LABELS[r.key] ?? r.key,
            })),
        [partsColumnPrefs.columnConfigForDrawer]
    );

    const auditDrawerColumns = useMemo(() => {
        if (tableSource === 'inventory') {
            return inventory.drawerColumns;
        }
        if (viewMode === 'orders') {
            return {
                rows: orderColumnDrawerRows,
                onToggle: ordersColumnPrefs.toggleVisible,
                onReorderDrag: ordersColumnPrefs.reorderByKey,
                onResetColumns: ordersColumnPrefs.reset,
                getDefaultRows: () =>
                    orderColumnKeys.map((key) => ({
                        key,
                        label: AUDIT_ORDER_LABELS[key] ?? key,
                        visible: !orderDefaultHidden.includes(key),
                        pinned: AUDIT_ORDER_PINNED_KEYS.includes(key),
                    })),
            };
        }
        if (viewMode === 'parts') {
            return {
                rows: partColumnDrawerRows,
                onToggle: partsColumnPrefs.toggleVisible,
                onReorderDrag: partsColumnPrefs.reorderByKey,
                onResetColumns: partsColumnPrefs.reset,
                getDefaultRows: () =>
                    partColumnKeys.map((key) => ({
                        key,
                        label: AUDIT_PART_LABELS[key] ?? key,
                        visible: !partDefaultHidden.includes(key),
                        pinned: AUDIT_PART_PINNED_KEYS.includes(key),
                    })),
            };
        }
        return undefined;
    }, [
        tableSource,
        inventory.drawerColumns,
        viewMode,
        orderColumnDrawerRows,
        partColumnDrawerRows,
        orderColumnKeys,
        orderDefaultHidden,
        partColumnKeys,
        partDefaultHidden,
        ordersColumnPrefs.toggleVisible,
        ordersColumnPrefs.reorderByKey,
        ordersColumnPrefs.reset,
        partsColumnPrefs.toggleVisible,
        partsColumnPrefs.reorderByKey,
        partsColumnPrefs.reset,
    ]);

    const [partOptionsModal, setPartOptionsModal] = useState<{
        orderPartId: string;
        orderId: string;
    } | null>(null);

    // Search state (client-side for orders, server-side for parts)
    const [searchQuery, setSearchQuery] = useState('');

    // Handler for search that resets page
    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        setOrdersPage(1);
        setPartsPage(1);
    };

    // Pagination state
    const [ordersPage, setOrdersPage] = useState(1);
    const [partsPage, setPartsPage] = useState(1);
    const pageSize = 50;
    const ordersQueryLimit = pageSize + 1;

    // Global filter states
    const [selectedLocations, setSelectedLocations] = useState<string[] | 'all'>(() =>
        locationId ? [locationId] : []
    );

    useEffect(() => {
        if (locationId) {
            setSelectedLocations([locationId]);
        }
    }, [locationId]);
    const [selectedInsurances, setSelectedInsurances] = useState<string[]>([]);
    const [selectedMakes, setSelectedMakes] = useState<string[]>([]);
    const [selectedPartTypes, setSelectedPartTypes] = useState<string[]>([]);
    const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
    const [pivotOrderIds, setPivotOrderIds] = useState<string[] | undefined>(undefined);

    interface PersistedAuditFilters {
        selectedLocations: string[] | 'all';
        selectedInsurances: string[];
        selectedMakes: string[];
        selectedPartTypes: string[];
        selectedVendors: string[];
    }

    const liveFiltersRef = useRef<PersistedAuditFilters>();
    liveFiltersRef.current = {
        selectedLocations,
        selectedInsurances,
        selectedMakes,
        selectedPartTypes,
        selectedVendors,
    };
    const drawerFiltersSnapshotRef = useRef<PersistedAuditFilters | null>(null);

    const handleSettingsOpen = useCallback(() => {
        drawerFiltersSnapshotRef.current = liveFiltersRef.current ?? null;
        if (tableSource === 'inventory') {
            inventory.handleSettingsOpen();
        }
    }, [inventory, tableSource]);

    const handleSettingsSave = useCallback(() => {
        drawerFiltersSnapshotRef.current = null;
        if (tableSource === 'inventory') {
            inventory.handleSettingsSave();
        }
    }, [inventory, tableSource]);

    const handleSettingsDiscard = useCallback(() => {
        if (tableSource === 'inventory') {
            inventory.handleSettingsDiscard();
            return;
        }
        const snap = drawerFiltersSnapshotRef.current;
        if (snap) {
            setSelectedLocations(snap.selectedLocations);
            setSelectedInsurances(snap.selectedInsurances);
            setSelectedMakes(snap.selectedMakes);
            setSelectedPartTypes(snap.selectedPartTypes);
            setSelectedVendors(snap.selectedVendors);
            setPivotOrderIds(undefined);
            setOrdersPage(1);
            setPartsPage(1);
        }
        drawerFiltersSnapshotRef.current = null;
    }, [inventory, tableSource]);

    const handleCommitColumns = useCallback((rows: SettingsColumnDrawerRow[]) => {
        if (tableSource === 'inventory') {
            inventory.handleCommitColumns(rows);
            return;
        }
        const prefs = columnDrawerRowsToPrefs(rows);
        if (viewMode === 'orders') {
            ordersColumnPrefs.setPrefs(prefs);
        } else if (viewMode === 'parts') {
            partsColumnPrefs.setPrefs(prefs);
        }
    }, [tableSource, inventory, viewMode, ordersColumnPrefs, partsColumnPrefs]);

    const pagedPivotOrderIdsForOrders = useMemo(() => {
        if (!pivotOrderIds || pivotOrderIds.length === 0) return undefined;
        const startIdx = (ordersPage - 1) * pageSize;
        const endIdx = startIdx + ordersQueryLimit;
        const pageIds = pivotOrderIds.slice(startIdx, endIdx);
        return pageIds.length > 0 ? pageIds : undefined;
    }, [pivotOrderIds, ordersPage, pageSize, ordersQueryLimit]);

    useEffect(() => {
        if (!isActive || !pendingDrilldown) return;

        setViewMode(pendingDrilldown.targetView ?? 'orders');
        setOrdersPage(1);
        setPartsPage(1);
        setSelectedPartTypes(pendingDrilldown.partTypes ?? []);
        setSelectedVendors(pendingDrilldown.vendors ?? []);
        setSelectedInsurances(pendingDrilldown.insuranceIds ?? []);
        setSelectedMakes(pendingDrilldown.makes ?? []);
        setPivotOrderIds(pendingDrilldown.orderIds);

        if (!locationId) {
            setSelectedLocations(pendingDrilldown.locationIds ?? []);
        }

        onDrilldownApplied?.();
    }, [isActive, locationId, onDrilldownApplied, pendingDrilldown]);

    // Wrapper handlers that reset page when filters change
    const handleLocationChange = (locations: string[] | 'all') => {
        setSelectedLocations(locations);
        setPivotOrderIds(undefined);
        setOrdersPage(1);
        setPartsPage(1);
    };
    const handleInsuranceChange = (insurances: FilterSelectionUpdater) => {
        setSelectedInsurances((prev) => (typeof insurances === 'function' ? insurances(prev) : insurances));
        setPivotOrderIds(undefined);
        setOrdersPage(1);
        setPartsPage(1);
    };
    const handleMakesChange = (makes: FilterSelectionUpdater) => {
        setSelectedMakes((prev) => (typeof makes === 'function' ? makes(prev) : makes));
        setPivotOrderIds(undefined);
        setOrdersPage(1);
        setPartsPage(1);
    };
    const handlePartTypesChange = (partTypes: FilterSelectionUpdater) => {
        setSelectedPartTypes((prev) =>
            normalizePartTypeSelections(
                typeof partTypes === 'function' ? partTypes(prev) : partTypes,
                filterOptions.partTypes
            )
        );
        setPartsPage(1);
    };
    const handleVendorsChange = (vendors: FilterSelectionUpdater) => {
        setSelectedVendors((prev) =>
            normalizeVendorSelections(
                typeof vendors === 'function' ? vendors(prev) : vendors,
                vendorOptionsForSelector
            )
        );
        setPartsPage(1);
    };

    // Sort states for orders
    const [ordersSortBy, setOrdersSortBy] = useState<string | null>(null);
    const [ordersSortOrder, setOrdersSortOrder] = useState<'asc' | 'desc'>('asc');

    // Sort states for parts
    const [partsSortBy, setPartsSortBy] = useState<string | null>(null);
    const [partsSortOrder, setPartsSortOrder] = useState<'asc' | 'desc'>('asc');

    // Range filter states for orders
    const [ordersRangeFilters, setOrdersRangeFilters] = useState<Record<string, RangeFilter>>({});
    const [partsRangeFilters, setPartsRangeFilters] = useState<Record<string, RangeFilter>>({});

    const ordersAuditingOptions = useMemo(
        () => ({
            // When drilldown orderIds exist, send only ids for the current page window.
            // This keeps request bodies small while paging.
            orderIds: pagedPivotOrderIdsForOrders,
            locationIds:
                selectedLocations !== 'all' && selectedLocations.length > 0 ? selectedLocations : undefined,
            insuranceIds: selectedInsurances.length > 0 ? selectedInsurances : undefined,
            makes: selectedMakes.length > 0 ? selectedMakes : undefined,
            // orderIds are already windowed by page, so keep backend offset at first page.
            page: pagedPivotOrderIdsForOrders ? 1 : ordersPage,
            limit: ordersQueryLimit,
            searchQuery: searchQuery || undefined,
            sortBy: ordersSortBy ?? undefined,
            sortOrder: ordersSortOrder,
            rangeFilters: Object.keys(ordersRangeFilters).length > 0 ? ordersRangeFilters : undefined,
            preset,
            excludeEvs,
            enabled: isActive,
        }),
        [
            selectedLocations,
            selectedInsurances,
            selectedMakes,
            pagedPivotOrderIdsForOrders,
            ordersPage,
            ordersQueryLimit,
            searchQuery,
            ordersSortBy,
            ordersSortOrder,
            ordersRangeFilters,
            preset,
            excludeEvs,
            isActive,
        ]
    );

    const partsAuditingOptions = useMemo(
        () => ({
            // Keep parts scoped to the same drilldown order-id window as orders.
            // Without this, switching orders -> parts can drop rows when drilldown orderIds
            // include orders outside the base date range.
            orderIds: pagedPivotOrderIdsForOrders,
            locationIds:
                selectedLocations !== 'all' && selectedLocations.length > 0 ? selectedLocations : undefined,
            insuranceIds: selectedInsurances.length > 0 ? selectedInsurances : undefined,
            makes: selectedMakes.length > 0 ? selectedMakes : undefined,
            partTypes: selectedPartTypes.length > 0 ? selectedPartTypes : undefined,
            vendors: selectedVendors.length > 0 ? selectedVendors : undefined,
            page: partsPage,
            pageSize,
            searchQuery: searchQuery || undefined,
            sortBy: partsSortBy ?? undefined,
            sortOrder: partsSortOrder,
            rangeFilters: Object.keys(partsRangeFilters).length > 0 ? partsRangeFilters : undefined,
            preset,
            excludeEvs,
            enabled: isActive,
        }),
        [
            selectedLocations,
            selectedInsurances,
            selectedMakes,
            pagedPivotOrderIdsForOrders,
            selectedPartTypes,
            selectedVendors,
            partsPage,
            pageSize,
            searchQuery,
            partsSortBy,
            partsSortOrder,
            partsRangeFilters,
            preset,
            excludeEvs,
            isActive,
        ]
    );

    useEffect(() => {
        setOrdersPage(1);
        setPartsPage(1);
        setOrdersRangeFilters((prev) => {
            if (!prev.lastUpdated) return prev;
            const next = { ...prev };
            delete next.lastUpdated;
            return next;
        });
        setPartsRangeFilters({});
    }, [startDate, endDate, preset]);

    // Fetch orders data (all orders - pagination handled client-side)
    const { data: ordersData, isLoading: ordersLoading } = useAuditingOrders(
        startDate,
        endDate,
        ordersAuditingOptions
    );

    // Calculate derived values from raw totals
    const { widths: orderColWidths, updateWidth: updateOrderColWidth } = useColumnWidths('audit_orders');
    const { widths: partColWidths, updateWidth: updatePartColWidth } = useColumnWidths('audit_parts');

    const calcOrderValue = (order: AuditingOrderItem) => order.totalListPrice;
    const calcGrossMarginPct = (order: AuditingOrderItem) =>
        order.totalListPrice > 0 ? ((order.totalListPrice - order.totalBuyPrice) / order.totalListPrice) * 100 : 0;

    // Fetch parts data (only parts with missedOpportunity > 0, client-side pagination)
    const { data: partsData, isLoading: partsLoading } = useAuditingParts(
        startDate,
        endDate,
        partsAuditingOptions
    );

    // Fetch bookmarks data
    const { data: bookmarksData } = useAuditingBookmarks();
    const createBookmark = useCreateAuditingBookmark();
    const deleteBookmark = useDeleteAuditingBookmark();
    const toast = useToast();
    const [isExporting, setIsExporting] = useState(false);

    // Get bookmarks list for quick lookup
    const bookmarks = bookmarksData?.bookmarks ?? [];

    // Handle bookmark actions
    const handleBookmarkAction = (
        targetId: string,
        orderId: string,
        targetType: AuditingBookmarkTargetType,
        bookmarkType: AuditingBookmarkType,
        isCurrentlyBookmarked: boolean,
        metadata: { roNumber?: string; vehicleMake?: string; insuranceName?: string; partName?: string; }
    ) => {
        if (isCurrentlyBookmarked) {
            deleteBookmark.mutate(
                { targetId, targetType, bookmarkType },
                {
                    onError: () => {
                        toast({
                            title: 'Error',
                            description: 'Failed to remove bookmark. Please try again.',
                            status: 'error',
                            duration: 3000,
                        });
                    },
                }
            );
        } else {
            createBookmark.mutate(
                {
                    targetId,
                    orderId,
                    targetType,
                    bookmarkType,
                    ...metadata,
                },
                {
                    onError: () => {
                        toast({
                            title: 'Error',
                            description: 'Failed to save bookmark. Please try again.',
                            status: 'error',
                            duration: 3000,
                        });
                    },
                }
            );
        }
    };

    // Derive filter options from response
    const filterOptions = useMemo(() => {
        const ordersOpts = ordersData?.filterOptions;
        const partsOpts = partsData?.filterOptions;
        return {
            locations: (ordersOpts?.locations ?? partsOpts?.locations)?.map((l: { id: string; name: string; }) => ({ value: l.id, label: l.name })) ?? [],
            insurances: (ordersOpts?.insurances ?? partsOpts?.insurances)?.map((i: { id: string; name: string; }) => ({ value: i.id, label: i.name })) ?? [],
            makes: (ordersOpts?.makes ?? partsOpts?.makes)?.map((m: string) => ({ value: m, label: m })) ?? [],
            partTypes: partsOpts?.partTypes?.map((pt: string) => ({ value: pt, label: getPartTypeLabel(pt) })) ?? [],
            vendors: partsOpts?.vendors?.map((v: string) => ({ value: v, label: v })) ?? [],
        };
    }, [ordersData?.filterOptions, partsData?.filterOptions]);

    useEffect(() => {
        if (selectedPartTypes.length === 0 || filterOptions.partTypes.length === 0) return;
        const normalized = normalizePartTypeSelections(selectedPartTypes, filterOptions.partTypes);
        const changed =
            normalized.length !== selectedPartTypes.length ||
            normalized.some((value, index) => value !== selectedPartTypes[index]);
        if (changed) {
            setSelectedPartTypes(normalized);
        }
    }, [filterOptions.partTypes, selectedPartTypes]);

    /**
     * API filterOptions only include insurers/makes present in the *current* filtered rows, so after
     * picking one, others vanish from the response. Accumulate every id we have ever seen (per reporting
     * window) so the dropdown stays a full multi-select like locations.
     */
    const [insuranceCatalog, setInsuranceCatalog] = useState(() => new Map<string, string>());
    const [makeCatalog, setMakeCatalog] = useState(() => new Set<string>());

    useEffect(() => {
        setInsuranceCatalog(new Map());
        setMakeCatalog(new Set());
    }, [startDate, endDate, preset]);

    useEffect(() => {
        setInsuranceCatalog((prev) => {
            const next = new Map(prev);
            for (const o of filterOptions.insurances) {
                next.set(o.value, o.label);
            }
            return next;
        });
    }, [filterOptions.insurances]);

    useEffect(() => {
        setMakeCatalog((prev) => {
            const next = new Set(prev);
            for (const o of filterOptions.makes) {
                next.add(o.value);
            }
            return next;
        });
    }, [filterOptions.makes]);

    const insuranceOptionsForSelector = useMemo(() => {
        const map = new Map<string, { value: string; label: string; }>();
        insuranceCatalog.forEach((name, id) => {
            map.set(id, { value: id, label: name });
        });
        for (const id of selectedInsurances) {
            if (!map.has(id)) {
                map.set(id, { value: id, label: id });
            }
        }
        return Array.from(map.values()).sort((a, b) =>
            (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })
        );
    }, [insuranceCatalog, selectedInsurances]);

    const makeOptionsForSelector = useMemo(() => {
        const map = new Map<string, { value: string; label: string; }>();
        makeCatalog.forEach((m) => {
            map.set(m, { value: m, label: m });
        });
        for (const m of selectedMakes) {
            if (!map.has(m)) {
                map.set(m, { value: m, label: m });
            }
        }
        return Array.from(map.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
    }, [makeCatalog, selectedMakes]);

    const vendorOptionsForSelector = useMemo(() => {
        const map = new Map<string, { value: string; label: string; }>();
        filterOptions.vendors.forEach((vendor) => {
            map.set(vendor.value, vendor);
        });
        selectedVendors.forEach((vendor) => {
            if (!map.has(vendor)) {
                map.set(vendor, { value: vendor, label: vendor });
            }
        });
        return Array.from(map.values()).sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        );
    }, [filterOptions.vendors, selectedVendors]);

    useEffect(() => {
        if (selectedVendors.length === 0 || vendorOptionsForSelector.length === 0) return;
        const normalized = normalizeVendorSelections(selectedVendors, vendorOptionsForSelector);
        const changed =
            normalized.length !== selectedVendors.length ||
            normalized.some((value, index) => value !== selectedVendors[index]);
        if (changed) {
            setSelectedVendors(normalized);
        }
    }, [selectedVendors, vendorOptionsForSelector]);

    // Sort handler for orders (client-side sorting)
    const handleOrdersSort = (column: string) => (direction: SortDirection) => {
        if (direction === null) {
            setOrdersSortBy(null);
        } else {
            setOrdersSortBy(column);
            setOrdersSortOrder(direction);
        }
        setOrdersPage(1); // Reset to first page when sorting changes
    };

    // Sort handler for parts (client-side sorting)
    const handlePartsSort = (column: string) => (direction: SortDirection) => {
        if (direction === null) {
            setPartsSortBy(null);
        } else {
            setPartsSortBy(column);
            setPartsSortOrder(direction);
        }
        setPartsPage(1); // Reset to first page when sorting changes
    };

    // Get current sort direction for a column
    const getOrdersSortDirection = (column: string): SortDirection => {
        return ordersSortBy === column ? ordersSortOrder : null;
    };

    const getPartsSortDirection = (column: string): SortDirection => {
        return partsSortBy === column ? partsSortOrder : null;
    };

    const getNextSortDirection = (direction: SortDirection): SortDirection => {
        if (direction === 'desc') return 'asc';
        if (direction === 'asc') return null;
        return 'desc';
    };

    const getSortIconDirection = (direction: SortDirection): SortIconDirection => {
        if (direction === 'asc' || direction === 'desc') return direction;
        return 'none';
    };

    const toggleOrdersSort = (column: string) => {
        handleOrdersSort(column)(getNextSortDirection(getOrdersSortDirection(column)));
    };

    const togglePartsSort = (column: string) => {
        handlePartsSort(column)(getNextSortDirection(getPartsSortDirection(column)));
    };

    const renderSortButton = (
        direction: SortDirection,
        onClick: () => void,
        ariaLabel: string
    ) => (
        <IconButton
            aria-label={ariaLabel}
            icon={<SortArrowIcon direction={getSortIconDirection(direction)} size={14} />}
            variant="ghost"
            size="xs"
            minW="auto"
            h="18px"
            px={1}
            color={direction ? '#0071E3' : 'gray.400'}
            _hover={{ color: '#0071E3', bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            onClick={onClick}
        />
    );

    // Range filter handler for orders
    const handleOrdersRangeChange = (column: string) => (range: RangeFilter | null) => {
        setOrdersRangeFilters(prev => {
            const next = { ...prev };
            if (range === null || (!range.min && !range.max)) {
                delete next[column];
            } else {
                next[column] = range;
            }
            return next;
        });
        setOrdersPage(1);
    };

    const handlePartsRangeChange = (column: string) => (range: RangeFilter | null) => {
        setPartsRangeFilters(prev => {
            const next = { ...prev };
            if (range === null || (!range.min && !range.max)) {
                delete next[column];
            } else {
                next[column] = range;
            }
            return next;
        });
        setPartsPage(1);
    };

    // Table header style for dense display
    const thStyle = {
        py: 2,
        px: 2,
        fontSize: 'xs',
        fontWeight: 'semibold',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: 'gray.600',
        borderBottom: '1px solid',
        borderColor: 'gray.200',
        bg: 'gray.50',
        whiteSpace: 'nowrap' as const,
    };

    // Table cell style for dense display
    const tdStyle = {
        py: 1.5,
        px: 2,
        fontSize: 'xs',
        borderBottom: '1px solid',
        borderColor: 'gray.100',
    };

    // Show loading when initial load
    const isLoading =
        tableSource === 'inventory'
            ? inventory.isLoading
            : viewMode === 'orders'
              ? ordersLoading
              : partsLoading;

    // Orders are filtered/sorted/paginated server-side.
    const { orders, ordersHasNextPage } = useMemo(() => {
        const fetched = ordersData?.orders ?? [];
        return {
            orders: fetched.slice(0, pageSize),
            ordersHasNextPage: fetched.length > pageSize,
        };
    }, [ordersData?.orders, pageSize]);
    const totalOrdersPages = useMemo(
        () => (ordersHasNextPage ? ordersPage + 1 : ordersPage),
        [ordersHasNextPage, ordersPage]
    );

    // Parts are filtered/sorted/paginated server-side.
    const parts = partsData?.parts ?? [];
    const totalPartsPages = partsData?.totalPages ?? 0;

    useEffect(() => {
        if (viewMode !== 'parts') return;
        const serverPage = partsData?.page;
        const serverTotalPages = partsData?.totalPages;
        // During page transitions, query data can be temporarily undefined.
        // Do not treat that transient state as "0 pages" and reset pagination.
        if (typeof serverPage !== 'number' && typeof serverTotalPages !== 'number') return;
        if (typeof serverPage === 'number' && serverPage > 0 && serverPage !== partsPage) {
            setPartsPage(serverPage);
            return;
        }
        if ((serverTotalPages ?? 0) === 0 && partsPage !== 1) {
            setPartsPage(1);
        }
    }, [viewMode, partsData?.page, partsData?.totalPages, partsPage]);

    const totalPages = viewMode === 'orders'
        ? totalOrdersPages
        : totalPartsPages;
    const currentPage = viewMode === 'orders' ? ordersPage : partsPage;

    const activeTableFilterCount = useMemo(() => {
        if (tableSource === 'inventory') return inventory.badgeCount;
        let n = 0;
        if (!locationId && Array.isArray(selectedLocations) && selectedLocations.length > 0) {
            n += 1;
        }
        if (selectedInsurances.length > 0) {
            n += 1;
        }
        if (selectedMakes.length > 0) {
            n += 1;
        }
        if (viewMode === 'parts' && selectedPartTypes.length > 0) {
            n += 1;
        }
        if (viewMode === 'parts' && selectedVendors.length > 0) {
            n += 1;
        }
        return n;
    }, [
        tableSource,
        inventory.badgeCount,
        locationId,
        selectedLocations,
        selectedInsurances,
        selectedMakes,
        selectedPartTypes,
        selectedVendors,
        viewMode,
    ]);

    const tableSourceSwitch = inventoryAvailable ? (
        <HStack justify="space-between" align="center" w="100%">
            <Text fontSize="md" fontWeight="medium" color="gray.800">
                Table
            </Text>
            <HStack spacing={0} bg="gray.100" borderRadius="md" p="2px">
                {([
                    { key: 'procurement' as const, label: 'Procurement' },
                    { key: 'inventory' as const, label: 'Inventory' },
                ]).map((opt) => {
                    const isActiveSource = tableSource === opt.key;
                    return (
                        <Box
                            key={opt.key}
                            as="button"
                            type="button"
                            px={3}
                            py={1}
                            borderRadius="md"
                            bg={isActiveSource ? 'white' : 'transparent'}
                            boxShadow={isActiveSource ? 'sm' : 'none'}
                            fontSize="sm"
                            fontWeight="medium"
                            color={isActiveSource ? 'gray.800' : 'gray.500'}
                            transition="all 0.15s ease"
                            _hover={!isActiveSource ? { color: 'gray.700' } : undefined}
                            onClick={() => {
                                if (!isActiveSource) handleTableSourceChange(opt.key);
                            }}
                        >
                            {opt.label}
                        </Box>
                    );
                })}
            </HStack>
        </HStack>
    ) : null;

    const visibleOrderCols = ordersColumnPrefs.visibleOrderedKeys;
    const visiblePartCols = partsColumnPrefs.visibleOrderedKeys;

    const orderHeaderLeft = new Set(['ro', 'writers', 'vin', 'location', 'vehicle', 'make', 'insurance']);
    const orderHeaderNoSort = new Set(['ro', 'writers', 'vin', 'location', 'vehicle', 'insurance']);

    const renderOrdersHeaderCell = (columnKey: string): ReactNode => {
        const label = AUDIT_ORDER_LABELS[columnKey] ?? columnKey;
        const align = orderHeaderLeft.has(columnKey) ? {} : { textAlign: 'center' as const };

        switch (columnKey) {
            case 'actions':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center" w="60px" aria-label="Row actions" />
                );
            case 'lastUpdated':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('lastUpdated'),
                                () => toggleOrdersSort('lastUpdated'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['lastUpdated']}
                                onRangeChange={handleOrdersRangeChange('lastUpdated')}
                                rangeType="date"
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'dateStarted':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('dateStarted'),
                                () => toggleOrdersSort('dateStarted'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['dateStarted']}
                                onRangeChange={handleOrdersRangeChange('dateStarted')}
                                rangeType="date"
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'daysActive':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('daysActive'),
                                () => toggleOrdersSort('daysActive'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['daysActive']}
                                onRangeChange={handleOrdersRangeChange('daysActive')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'partsOrdered':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('partsOrdered'),
                                () => toggleOrdersSort('partsOrdered'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['partsOrdered']}
                                onRangeChange={handleOrdersRangeChange('partsOrdered')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'orderValue':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('orderValue'),
                                () => toggleOrdersSort('orderValue'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['orderValue']}
                                onRangeChange={handleOrdersRangeChange('orderValue')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'grossMarginPct':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('grossMarginPct'),
                                () => toggleOrdersSort('grossMarginPct'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['grossMarginPct']}
                                onRangeChange={handleOrdersRangeChange('grossMarginPct')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'totalMissedOpp':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('totalMissedOpp'),
                                () => toggleOrdersSort('totalMissedOpp'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['totalMissedOpp']}
                                onRangeChange={handleOrdersRangeChange('totalMissedOpp')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'totalCostVariance':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('totalCostVariance'),
                                () => toggleOrdersSort('totalCostVariance'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['totalCostVariance']}
                                onRangeChange={handleOrdersRangeChange('totalCostVariance')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'partsInvoiced':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getOrdersSortDirection('partsInvoiced'),
                                () => toggleOrdersSort('partsInvoiced'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={ordersRangeFilters['partsInvoiced']}
                                onRangeChange={handleOrdersRangeChange('partsInvoiced')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            default:
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={orderColWidths[columnKey]} onResize={updateOrderColWidth} {...thStyle} {...align}>
                        <HStack spacing={1} justify={orderHeaderLeft.has(columnKey) ? 'flex-start' : 'center'}>
                            <Text>{label}</Text>
                            {!orderHeaderNoSort.has(columnKey) &&
                                renderSortButton(
                                    getOrdersSortDirection(columnKey),
                                    () => toggleOrdersSort(columnKey),
                                    `Sort ${label}`
                                )}
                        </HStack>
                    </ResizableTh>
                );
        }
    };

    const renderOrdersBodyCell = (
        columnKey: string,
        order: AuditingOrderItem,
        bookmarkState: { isSaved: boolean; isFlagged: boolean; }
    ): ReactNode => {
        const { isSaved, isFlagged } = bookmarkState;
        const align = orderHeaderLeft.has(columnKey) ? {} : { textAlign: 'center' as const };
        const ymm =
            [order.vehicleYear, order.vehicleMake, order.vehicleModel].filter(Boolean).join(' ') || '—';

        switch (columnKey) {
            case 'ro':
                return (
                    <Td {...tdStyle} {...align}>
                        <HStack spacing={1}>
                            {isSaved && <Icon as={TbBookmark as React.ElementType} boxSize={3} color="blue.500" />}
                            {isFlagged && <Icon as={TbFlag as React.ElementType} boxSize={3} color="orange.500" />}
                            <Link
                                as={RouterLink}
                                to={`/order/${order.orderId}`}
                                state={{ initialStatus: 'selection' }}
                                target="_blank"
                                color="#0071E3"
                                fontWeight="medium"
                                _hover={{ textDecoration: 'underline' }}
                            >
                                {order.roNumber}
                            </Link>
                        </HStack>
                    </Td>
                );
            case 'vin':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontFamily="mono" fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {order.vin || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="150px" fontFamily="mono" fontSize="xs">
                                {order.vin || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'location':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {order.locationName || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="120px">
                                {order.locationName || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'writers': {
                const writers = writerNamesDisplay(order.writerNames);
                return (
                    <Td {...tdStyle} {...align}>
                        <Tooltip label={writers} placement="top" hasArrow isDisabled={writers === '—'}>
                            <Text noOfLines={1} maxW="120px">
                                {writers}
                            </Text>
                        </Tooltip>
                    </Td>
                );
            }
            case 'vehicle':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {ymm}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="180px">
                                {ymm}
                            </Text>
                        )}
                    </Td>
                );
            case 'make':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {order.vehicleMake || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="120px">
                                {order.vehicleMake || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'insurance':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {order.insuranceName || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="150px">
                                {order.insuranceName || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'dateStarted':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatDate(order.dateStarted || order.lastUpdated)}
                    </Td>
                );
            case 'lastUpdated':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatDate(order.lastUpdated)}
                    </Td>
                );
            case 'daysActive':
                return (
                    <Td {...tdStyle} {...align}>
                        {order.daysActive}
                    </Td>
                );
            case 'partsQuoted':
            case 'totalQuotes':
            case 'partsOrdered':
            case 'partsConfirmed':
            case 'partsExcluded':
            case 'partsReturnedCancelled':
            case 'totalVendors':
                return (
                    <Td {...tdStyle} {...align}>
                        {(order as unknown as Record<string, number>)[columnKey] ?? 0}
                    </Td>
                );
            case 'orderValue':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(calcOrderValue(order))}
                    </Td>
                );
            case 'grossProfit':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(auditingOrderGrossProfit(order))}
                    </Td>
                );
            case 'grossMarginPct':
                return (
                    <Td {...tdStyle} {...align}>
                        <Text color={calcGrossMarginPct(order) < 0 ? 'red.500' : 'inherit'}>
                            {formatPercent(calcGrossMarginPct(order))}
                        </Text>
                    </Td>
                );
            case 'totalMissedOpp':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(order.totalMissedOpp ?? 0)}
                    </Td>
                );
            case 'totalCostVariance':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrencyPrecise(order.totalCostVariance)}
                    </Td>
                );
            case 'partsInvoiced':
                return (
                    <Td {...tdStyle} {...align}>
                        {order.partsInvoiced}
                    </Td>
                );
            case 'actions':
                return (
                    <Td {...tdStyle} textAlign="center">
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                icon={<Icon as={TbDotsVertical as React.ElementType} />}
                                variant="ghost"
                                size="xs"
                                aria-label="Actions"
                            />
                            <MenuList py={1} minW="160px">
                                <MenuItem
                                    fontSize="sm"
                                    onClick={() =>
                                        handleBookmarkAction(
                                            order.orderId,
                                            order.orderId,
                                            'order',
                                            'saved',
                                            isSaved,
                                            {
                                                roNumber: order.roNumber,
                                                vehicleMake: order.vehicleMake ?? undefined,
                                                insuranceName: order.insuranceName ?? undefined,
                                            }
                                        )
                                    }
                                >
                                    <HStack spacing={2}>
                                        <Icon
                                            as={TbBookmark as React.ElementType}
                                            boxSize={4}
                                            color={isSaved ? 'blue.500' : 'gray.500'}
                                        />
                                        <Text>{isSaved ? 'Remove from Saved' : 'Save for Later'}</Text>
                                    </HStack>
                                </MenuItem>
                                <MenuItem
                                    fontSize="sm"
                                    onClick={() =>
                                        handleBookmarkAction(
                                            order.orderId,
                                            order.orderId,
                                            'order',
                                            'flagged',
                                            isFlagged,
                                            {
                                                roNumber: order.roNumber,
                                                vehicleMake: order.vehicleMake ?? undefined,
                                                insuranceName: order.insuranceName ?? undefined,
                                            }
                                        )
                                    }
                                >
                                    <HStack spacing={2}>
                                        <Icon
                                            as={TbFlag as React.ElementType}
                                            boxSize={4}
                                            color={isFlagged ? 'orange.500' : 'gray.500'}
                                        />
                                        <Text>{isFlagged ? 'Remove Flag' : 'Mark as Flagged'}</Text>
                                    </HStack>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Td>
                );
            default:
                return (
                    <Td {...tdStyle} {...align}>
                        —
                    </Td>
                );
        }
    };

    const partHeaderLeft = new Set([
        'ro',
        'writers',
        'vin',
        'location',
        'vehicleYmm',
        'make',
        'insurance',
        'quoteSource',
        'partName',
        'partType',
        'vendor',
    ]);
    const partHeaderNoSort = new Set(['ro', 'writers', 'vin', 'location', 'vehicleYmm', 'insurance', 'make', 'partName']);

    const renderPartsHeaderCell = (columnKey: string): ReactNode => {
        const label = AUDIT_PART_LABELS[columnKey] ?? columnKey;
        const align = partHeaderLeft.has(columnKey) ? {} : { textAlign: 'center' as const };

        switch (columnKey) {
            case 'actions':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center" w="60px" aria-label="Row actions" />
                );
            case 'deliveryDays':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('deliveryDays'),
                                () => togglePartsSort('deliveryDays'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['deliveryDays']}
                                onRangeChange={handlePartsRangeChange('deliveryDays')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'dateStarted':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('dateStarted'),
                                () => togglePartsSort('dateStarted'),
                                `Sort ${label}`
                            )}
                        </HStack>
                    </ResizableTh>
                );
            case 'lastUpdated':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('lastUpdated'),
                                () => togglePartsSort('lastUpdated'),
                                `Sort ${label}`
                            )}
                        </HStack>
                    </ResizableTh>
                );
            case 'daysActive':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('daysActive'),
                                () => togglePartsSort('daysActive'),
                                `Sort ${label}`
                            )}
                        </HStack>
                    </ResizableTh>
                );
            case 'quantity':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('quantity'),
                                () => togglePartsSort('quantity'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['quantity']}
                                onRangeChange={handlePartsRangeChange('quantity')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'quotedOptionsCount':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('quotedOptionsCount'),
                                () => togglePartsSort('quotedOptionsCount'),
                                `Sort ${label}`
                            )}
                        </HStack>
                    </ResizableTh>
                );
            case 'originalListPrice':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('originalListPrice'),
                                () => togglePartsSort('originalListPrice'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['originalListPrice']}
                                onRangeChange={handlePartsRangeChange('originalListPrice')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'listPrice':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('listPrice'),
                                () => togglePartsSort('listPrice'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['listPrice']}
                                onRangeChange={handlePartsRangeChange('listPrice')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'buyPrice':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('buyPrice'),
                                () => togglePartsSort('buyPrice'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['buyPrice']}
                                onRangeChange={handlePartsRangeChange('buyPrice')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'partListPricePctOfOE':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('partListPricePctOfOE'),
                                () => togglePartsSort('partListPricePctOfOE'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['partListPricePctOfOE']}
                                onRangeChange={handlePartsRangeChange('partListPricePctOfOE')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'grossMarginPct':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('grossMarginPct'),
                                () => togglePartsSort('grossMarginPct'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['grossMarginPct']}
                                onRangeChange={handlePartsRangeChange('grossMarginPct')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'missedOpportunity':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('missedOpportunity'),
                                () => togglePartsSort('missedOpportunity'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['missedOpportunity']}
                                onRangeChange={handlePartsRangeChange('missedOpportunity')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            case 'costVariance':
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} textAlign="center">
                        <HStack spacing={1} justify="center">
                            <Text>{label}</Text>
                            {renderSortButton(
                                getPartsSortDirection('costVariance'),
                                () => togglePartsSort('costVariance'),
                                `Sort ${label}`
                            )}
                            <SortDropdown
                                rangeFilter={partsRangeFilters['costVariance']}
                                onRangeChange={handlePartsRangeChange('costVariance')}
                            />
                        </HStack>
                    </ResizableTh>
                );
            default:
                return (
                    <ResizableTh columnKey={columnKey} storedWidth={partColWidths[columnKey]} onResize={updatePartColWidth} {...thStyle} {...align}>
                        <HStack spacing={1} justify={partHeaderLeft.has(columnKey) ? 'flex-start' : 'center'}>
                            <Text>{label}</Text>
                            {!partHeaderNoSort.has(columnKey) &&
                                renderSortButton(
                                    getPartsSortDirection(columnKey),
                                    () => togglePartsSort(columnKey),
                                    `Sort ${label}`
                                )}
                        </HStack>
                    </ResizableTh>
                );
        }
    };

    const renderPartsBodyCell = (
        columnKey: string,
        part: AuditingPartItem,
        bookmarkState: { isSaved: boolean; isFlagged: boolean; }
    ): ReactNode => {
        const { isSaved, isFlagged } = bookmarkState;
        const align = partHeaderLeft.has(columnKey) ? {} : { textAlign: 'center' as const };

        switch (columnKey) {
            case 'ro':
                return (
                    <Td {...tdStyle} {...align}>
                        <HStack spacing={1}>
                            {isSaved && <Icon as={TbBookmark as React.ElementType} boxSize={3} color="blue.500" />}
                            {isFlagged && <Icon as={TbFlag as React.ElementType} boxSize={3} color="orange.500" />}
                            <Link
                                as="button"
                                type="button"
                                onClick={() =>
                                    setPartOptionsModal({
                                        orderPartId: part.partId,
                                        orderId: part.orderId,
                                    })
                                }
                                color="#0071E3"
                                fontWeight="medium"
                                textAlign="left"
                                _hover={{ textDecoration: 'underline' }}
                            >
                                {part.roNumber}
                            </Link>
                        </HStack>
                    </Td>
                );
            case 'vin':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontFamily="mono" fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.vin || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="150px" fontFamily="mono" fontSize="xs">
                                {part.vin || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'location':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.locationName || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="100px">
                                {part.locationName || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'writers': {
                const writers = writerNamesDisplay(part.writerNames);
                return (
                    <Td {...tdStyle} {...align}>
                        <Tooltip label={writers} placement="top" hasArrow isDisabled={writers === '—'}>
                            <Text noOfLines={1} maxW="120px">
                                {writers}
                            </Text>
                        </Tooltip>
                    </Td>
                );
            }
            case 'vehicleYmm':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {partVehicleYmmDisplay(part)}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="160px">
                                {partVehicleYmmDisplay(part)}
                            </Text>
                        )}
                    </Td>
                );
            case 'make':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.vehicleMake || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="100px">
                                {part.vehicleMake || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'insurance':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.insuranceName || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="120px">
                                {part.insuranceName || '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'quoteSource':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.quoteSource ? (
                            <SourceBadge source={part.quoteSource as AutomationTarget} />
                        ) : (
                            <Text fontSize={tableDetailMode ? 'xs' : undefined}>—</Text>
                        )}
                    </Td>
                );
            case 'partName':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.partName}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="180px" title={part.partName}>
                                {truncateText(part.partName)}
                            </Text>
                        )}
                    </Td>
                );
            case 'partType':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.partType ? <PartType partType={part.partType} /> : '—'}
                    </Td>
                );
            case 'vendor':
                return (
                    <Td {...tdStyle} {...align}>
                        {tableDetailMode ? (
                            <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                {part.vendorName || '—'}
                            </Text>
                        ) : (
                            <Text noOfLines={1} maxW="140px" title={part.vendorName || ''}>
                                {part.vendorName ? truncateText(part.vendorName, 20) : '—'}
                            </Text>
                        )}
                    </Td>
                );
            case 'deliveryDays':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.deliveryDays != null ? `${part.deliveryDays}d` : '—'}
                    </Td>
                );
            case 'dateStarted':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatDate(part.dateStarted)}
                    </Td>
                );
            case 'lastUpdated':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatDate(part.lastUpdated)}
                    </Td>
                );
            case 'daysActive':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.daysActive}
                    </Td>
                );
            case 'quantity':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.quantity ?? 1}
                    </Td>
                );
            case 'quotedOptionsCount':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.quotedOptionsCount}
                    </Td>
                );
            case 'originalListPrice':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(part.originalListPrice)}
                    </Td>
                );
            case 'listPrice':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(part.listPrice)}
                    </Td>
                );
            case 'buyPrice':
                return (
                    <Td {...tdStyle} {...align}>
                        {formatCurrency(part.buyPrice)}
                    </Td>
                );
            case 'partListPricePctOfOE':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.partListPricePctOfOE != null
                            ? formatPercent(part.partListPricePctOfOE)
                            : '—'}
                    </Td>
                );
            case 'grossMarginPct':
                return (
                    <Td {...tdStyle} {...align}>
                        <Text color={part.grossMarginPct < 0 ? 'red.500' : 'inherit'}>
                            {formatPercent(part.grossMarginPct)}
                        </Text>
                    </Td>
                );
            case 'missedOpportunity':
                return (
                    <Td {...tdStyle} {...align}>
                        <Text color={part.missedOpportunity > 0 ? 'red.500' : 'inherit'}>
                            {formatCurrency(part.missedOpportunity)}
                        </Text>
                    </Td>
                );
            case 'isInvoiced':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.isInvoiced ? 'Yes' : 'No'}
                    </Td>
                );
            case 'costVariance':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.costVariance == null ? '—' : formatCurrencyPrecise(part.costVariance)}
                    </Td>
                );
            case 'invoicedCost':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.invoicedCost == null ? '—' : formatCurrencyPrecise(part.invoicedCost)}
                    </Td>
                );
            case 'invoiceDate':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.invoiceDate ? formatDate(part.invoiceDate) : '—'}
                    </Td>
                );
            case 'finalEta':
                return (
                    <Td {...tdStyle} {...align}>
                        {part.finalEta ? formatFinalEtaWallDate(part.finalEta) : '—'}
                    </Td>
                );
            case 'actions':
                return (
                    <Td {...tdStyle} textAlign="center">
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                icon={<Icon as={TbDotsVertical as React.ElementType} />}
                                variant="ghost"
                                size="xs"
                                aria-label="Actions"
                            />
                            <MenuList py={1} minW="160px">
                                <MenuItem
                                    fontSize="sm"
                                    onClick={() =>
                                        handleBookmarkAction(
                                            part.partId,
                                            part.orderId,
                                            'part',
                                            'saved',
                                            isSaved,
                                            {
                                                roNumber: part.roNumber,
                                                vehicleMake: part.vehicleMake ?? undefined,
                                                insuranceName: part.insuranceName ?? undefined,
                                                partName: part.partName,
                                            }
                                        )
                                    }
                                >
                                    <HStack spacing={2}>
                                        <Icon
                                            as={TbBookmark as React.ElementType}
                                            boxSize={4}
                                            color={isSaved ? 'blue.500' : 'gray.500'}
                                        />
                                        <Text>{isSaved ? 'Remove from Saved' : 'Save for Later'}</Text>
                                    </HStack>
                                </MenuItem>
                                <MenuItem
                                    fontSize="sm"
                                    onClick={() =>
                                        handleBookmarkAction(
                                            part.partId,
                                            part.orderId,
                                            'part',
                                            'flagged',
                                            isFlagged,
                                            {
                                                roNumber: part.roNumber,
                                                vehicleMake: part.vehicleMake ?? undefined,
                                                insuranceName: part.insuranceName ?? undefined,
                                                partName: part.partName,
                                            }
                                        )
                                    }
                                >
                                    <HStack spacing={2}>
                                        <Icon
                                            as={TbFlag as React.ElementType}
                                            boxSize={4}
                                            color={isFlagged ? 'orange.500' : 'gray.500'}
                                        />
                                        <Text>{isFlagged ? 'Remove Flag' : 'Mark as Flagged'}</Text>
                                    </HStack>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Td>
                );
            default:
                return (
                    <Td {...tdStyle} {...align}>
                        —
                    </Td>
                );
        }
    };

    const viewModeConfig: { mode: ViewMode; label: string; icon: React.ElementType; }[] = [
        { mode: 'orders', label: 'Orders', icon: TbReceipt as React.ElementType },
        { mode: 'parts', label: 'Parts', icon: TbPuzzle as React.ElementType },
        { mode: 'setAside', label: 'Set Aside', icon: TbBookmark as React.ElementType },
    ];

    const activeFilterIcons = useMemo(() => {
        const icons: { icon: React.ElementType; label: string; }[] = [];
        if (!locationId && Array.isArray(selectedLocations) && selectedLocations.length > 0) {
            const locNameMap = new Map(filterOptions.locations.map((l: { value: string; label: string; }) => [l.value, l.label]));
            const names = selectedLocations.map(id => locNameMap.get(id) || id);
            icons.push({ icon: TbBuilding as React.ElementType, label: `Locations: ${names.join(', ')}` });
        }
        if (selectedInsurances.length > 0) {
            const names = selectedInsurances.map(id => insuranceCatalog.get(id) || id);
            icons.push({ icon: TbShieldCheck as React.ElementType, label: `Insurance: ${names.join(', ')}` });
        }
        if (selectedMakes.length > 0) {
            icons.push({ icon: TbCar as React.ElementType, label: `Makes: ${selectedMakes.join(', ')}` });
        }
        if (viewMode === 'parts' && selectedPartTypes.length > 0) {
            const labelMap = new Map(filterOptions.partTypes.map((p: { value: string; label: string; }) => [p.value, p.label]));
            const names = selectedPartTypes.map((value) => labelMap.get(value) || value);
            icons.push({ icon: TbPuzzle as React.ElementType, label: `Part Types: ${names.join(', ')}` });
        }
        if (viewMode === 'parts' && selectedVendors.length > 0) {
            icons.push({ icon: TbTruck as React.ElementType, label: `Vendors: ${selectedVendors.join(', ')}` });
        }
        return icons;
    }, [locationId, selectedLocations, selectedInsurances, selectedMakes, selectedPartTypes, selectedVendors, viewMode, filterOptions.locations, filterOptions.partTypes, insuranceCatalog]);

    const BTN_SIZE = '32px';

    const getOrderExportValue = (columnKey: string, order: AuditingOrderItem): string | number => {
        switch (columnKey) {
            case 'ro':
                return order.roNumber ?? '';
            case 'vin':
                return order.vin ?? '';
            case 'location':
                return order.locationName ?? '';
            case 'writers':
                return order.writerNames && order.writerNames.length > 0 ? order.writerNames.join(', ') : '';
            case 'vehicle':
                return [order.vehicleYear, order.vehicleMake, order.vehicleModel].filter(Boolean).join(' ');
            case 'make':
                return order.vehicleMake ?? '';
            case 'insurance':
                return order.insuranceName ?? '';
            case 'dateStarted':
                return formatDate(order.dateStarted || order.lastUpdated);
            case 'lastUpdated':
                return formatDate(order.lastUpdated);
            case 'daysActive':
                return order.daysActive ?? 0;
            case 'partsQuoted':
            case 'totalQuotes':
            case 'partsOrdered':
            case 'partsConfirmed':
            case 'partsExcluded':
            case 'partsReturnedCancelled':
            case 'totalVendors':
                return (order as unknown as Record<string, number>)[columnKey] ?? 0;
            case 'orderValue':
                return calcOrderValue(order) ?? 0;
            case 'grossProfit':
                return auditingOrderGrossProfit(order) ?? 0;
            case 'grossMarginPct':
                return Number(calcGrossMarginPct(order).toFixed(1));
            case 'totalMissedOpp':
                return order.totalMissedOpp ?? 0;
            case 'totalCostVariance':
                return order.totalCostVariance;
            case 'partsInvoiced':
                return order.partsInvoiced;
            default:
                return '';
        }
    };

    const getPartExportValue = (columnKey: string, part: AuditingPartItem): string | number => {
        switch (columnKey) {
            case 'ro':
                return part.roNumber ?? '';
            case 'vin':
                return part.vin ?? '';
            case 'location':
                return part.locationName ?? '';
            case 'writers':
                return part.writerNames && part.writerNames.length > 0 ? part.writerNames.join(', ') : '';
            case 'vehicleYmm':
                return partVehicleYmmDisplay(part).replace('—', '');
            case 'make':
                return part.vehicleMake ?? '';
            case 'insurance':
                return part.insuranceName ?? '';
            case 'quoteSource':
                return part.quoteSource ?? '';
            case 'partName':
                return part.partName ?? '';
            case 'partType':
                return part.partType ? getPartTypeLabel(part.partType) : '';
            case 'quantity':
                return part.quantity ?? 1;
            case 'vendor':
                return part.vendorName ?? '';
            case 'deliveryDays':
                return part.deliveryDays != null ? part.deliveryDays : '';
            case 'dateStarted':
                return formatDate(part.dateStarted);
            case 'lastUpdated':
                return formatDate(part.lastUpdated);
            case 'daysActive':
                return part.daysActive ?? 0;
            case 'quotedOptionsCount':
                return part.quotedOptionsCount ?? 0;
            case 'originalListPrice':
                return part.originalListPrice ?? 0;
            case 'listPrice':
                return part.listPrice ?? 0;
            case 'buyPrice':
                return part.buyPrice ?? 0;
            case 'partListPricePctOfOE':
                return part.partListPricePctOfOE != null
                    ? Number(part.partListPricePctOfOE.toFixed(1))
                    : '';
            case 'grossMarginPct':
                return Number((part.grossMarginPct ?? 0).toFixed(1));
            case 'missedOpportunity':
                return part.missedOpportunity ?? 0;
            case 'isInvoiced':
                return part.isInvoiced ? 'Yes' : 'No';
            case 'costVariance':
                return part.costVariance ?? '';
            case 'invoicedCost':
                return part.invoicedCost ?? '';
            case 'invoiceDate':
                return part.invoiceDate ? formatDate(part.invoiceDate) : '';
            case 'finalEta':
                return part.finalEta ? formatFinalEtaWallDate(part.finalEta) : '';
            default:
                return '';
        }
    };

    const handleExportCsv = async () => {
        const today = new Date().toISOString().split('T')[0];
        const sessionId = sessionContext?.sessionId ?? '';

        // Set Aside view exports the bookmarks already loaded on the client.
        if (viewMode === 'setAside') {
            if (bookmarks.length === 0) {
                toast({ title: 'No saved or flagged items to export', status: 'info', duration: 2500, isClosable: true });
                return;
            }
            const rows = bookmarks.map((bookmark) => ({
                Type: bookmark.targetType === 'order' ? 'Order' : 'Part',
                RO: bookmark.roNumber || '',
                Make: bookmark.vehicleMake || '',
                Insurance: bookmark.insuranceName || '',
                'Part Name': bookmark.partName || '',
                Status: bookmark.bookmarkType === 'saved' ? 'Saved' : 'Flagged',
                Created: formatDate(bookmark.createdAt),
            }));
            exportToCsv(rows, `auditing_set_aside_${today}`);
            return;
        }

        setIsExporting(true);
        try {
            if (viewMode === 'orders') {
                const allOrders = await fetchAllAuditingOrders(sessionId, startDate, endDate, {
                    // Use the full drilldown set (not the paged window) so export covers everything.
                    orderIds: pivotOrderIds && pivotOrderIds.length > 0 ? pivotOrderIds : undefined,
                    locationIds:
                        selectedLocations !== 'all' && selectedLocations.length > 0 ? selectedLocations : undefined,
                    insuranceIds: selectedInsurances.length > 0 ? selectedInsurances : undefined,
                    makes: selectedMakes.length > 0 ? selectedMakes : undefined,
                    searchQuery: searchQuery || undefined,
                    sortBy: ordersSortBy ?? undefined,
                    sortOrder: ordersSortOrder,
                    rangeFilters: Object.keys(ordersRangeFilters).length > 0 ? ordersRangeFilters : undefined,
                    preset,
                    excludeEvs,
                });

                if (allOrders.length === 0) {
                    toast({ title: 'No orders to export', status: 'info', duration: 2500, isClosable: true });
                    return;
                }

                const cols = visibleOrderCols.filter((c) => c !== 'actions');
                const rows = allOrders.map((order) => {
                    const row: Record<string, string | number> = {};
                    cols.forEach((colKey) => {
                        const label = AUDIT_ORDER_LABELS[colKey] ?? colKey;
                        row[label] = getOrderExportValue(colKey, order);
                    });
                    return row;
                });
                exportToCsv(rows, `auditing_orders_${today}`);
                return;
            }

            // viewMode === 'parts'
            const allParts = await fetchAllAuditingParts(sessionId, startDate, endDate, {
                orderIds: pivotOrderIds && pivotOrderIds.length > 0 ? pivotOrderIds : undefined,
                locationIds:
                    selectedLocations !== 'all' && selectedLocations.length > 0 ? selectedLocations : undefined,
                insuranceIds: selectedInsurances.length > 0 ? selectedInsurances : undefined,
                makes: selectedMakes.length > 0 ? selectedMakes : undefined,
                partTypes: selectedPartTypes.length > 0 ? selectedPartTypes : undefined,
                vendors: selectedVendors.length > 0 ? selectedVendors : undefined,
                searchQuery: searchQuery || undefined,
                sortBy: partsSortBy ?? undefined,
                sortOrder: partsSortOrder,
                rangeFilters: Object.keys(partsRangeFilters).length > 0 ? partsRangeFilters : undefined,
                preset,
                excludeEvs,
            });

            if (allParts.length === 0) {
                toast({ title: 'No parts to export', status: 'info', duration: 2500, isClosable: true });
                return;
            }

            const cols = visiblePartCols.filter((c) => c !== 'actions');
            const rows = allParts.map((part) => {
                const row: Record<string, string | number> = {};
                cols.forEach((colKey) => {
                    const label = AUDIT_PART_LABELS[colKey] ?? colKey;
                    row[label] = getPartExportValue(colKey, part);
                });
                return row;
            });
            exportToCsv(rows, `auditing_parts_${today}`);
        } catch (err) {
            console.error('Failed to export auditing data', err);
            toast({ title: 'Export failed', description: 'Could not fetch all rows. Please try again.', status: 'error', duration: 3500, isClosable: true });
        } finally {
            setIsExporting(false);
        }
    };

    const auditingToolbarRow = (
        <Flex
            flexShrink={0}
            w="100%"
            align="flex-end"
            justify="space-between"
            borderBottom="1.5px solid"
            borderColor="gray.200"
            mb={2}
            pb={0}
        >
            {/* Left: tabs (hidden for Inventory table source) */}
            <Flex gap={0} flexShrink={0}>
                {tableSource === 'inventory' ? (
                    <Box
                        fontWeight="medium"
                        fontSize="sm"
                        color="gray.800"
                        borderBottom="1.5px solid"
                        borderColor="gray.500"
                        mb="-1.5px"
                        py={1.5}
                        px={3}
                    >
                        Inventory
                    </Box>
                ) : (
                    viewModeConfig.map(({ mode, label, icon }) => (
                    <Box
                        key={mode}
                        as="button"
                        type="button"
                        onClick={() => setViewMode(mode)}
                        fontWeight="medium"
                        fontSize="sm"
                        color={viewMode === mode ? 'gray.800' : 'gray.500'}
                        bg="transparent"
                        border="none"
                        borderBottom="1.5px solid"
                        borderColor={viewMode === mode ? 'gray.500' : 'transparent'}
                        mb="-1.5px"
                        py={1.5}
                        px={3}
                        cursor="pointer"
                        transition="all 0.15s"
                        _hover={{
                            color: viewMode === mode ? 'gray.800' : 'gray.700',
                        }}
                    >
                        <HStack spacing={1.5}>
                            <Icon as={icon} boxSize={3.5} />
                            <Text>
                                {label}
                            </Text>
                            {mode === 'setAside' && bookmarks.length > 0 && (
                                <Flex
                                    bg="gray.100"
                                    borderRadius="lg"
                                    h="18px"
                                    minW="18px"
                                    px="5px"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                >
                                    <Text
                                        fontSize="9px"
                                        fontWeight="bold"
                                        lineHeight="1"
                                        color="gray.800"
                                    >
                                        {bookmarks.length}
                                    </Text>
                                </Flex>
                            )}
                        </HStack>
                    </Box>
                    ))
                )}
            </Flex>
            {/* Right: filter indicators, search, settings, fullscreen */}
            <HStack spacing={2} align="flex-end" pb={1} pt={1}>
                {tableSource === 'procurement' &&
                    activeFilterIcons.map(({ icon: FilterIcon, label: filterLabel }, i) => (
                    <Tooltip key={i} label={filterLabel} placement="top" hasArrow fontSize="xs">
                        <Flex
                            align="center"
                            justify="center"
                            w={BTN_SIZE}
                            h={BTN_SIZE}
                            borderRadius="md"
                            bg="#E6F1FD"
                        >
                            <Icon as={FilterIcon} boxSize={4} color="#0071E3" />
                        </Flex>
                    </Tooltip>
                ))}
                {(tableSource === 'inventory' || viewMode !== 'setAside') && (
                    <InputGroup w="220px" maxW="100%" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
                        </InputLeftElement>
                        <Input
                            placeholder={
                                tableSource === 'inventory'
                                    ? 'Search by name or part #...'
                                    : 'Search by VIN or RO#...'
                            }
                            value={tableSource === 'inventory' ? inventory.searchQuery : searchQuery}
                            onChange={(e) => {
                                if (tableSource === 'inventory') {
                                    inventory.setSearchQuery(e.target.value);
                                    inventory.setPage(1);
                                } else {
                                    handleSearchChange(e.target.value);
                                }
                            }}
                            bg="white"
                            borderColor="gray.300"
                            borderRadius="md"
                            h={BTN_SIZE}
                            fontSize="sm"
                            fontWeight="normal"
                            _placeholder={{ color: 'gray.400' }}
                            _hover={{ borderColor: 'gray.400' }}
                            _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                        />
                    </InputGroup>
                )}
                <ReportingTableSettingsDrawer
                    settings={auditTableSettings}
                    onUpdateSettings={updateAuditTableSettings}
                    columns={auditDrawerColumns}
                    onCommitColumns={handleCommitColumns}
                    useAccordionLayout
                    headerContent={tableSourceSwitch}
                    badgeCount={activeTableFilterCount}
                    onBeforeOpen={handleSettingsOpen}
                    onSaveChanges={handleSettingsSave}
                    onDiscardChanges={handleSettingsDiscard}
                    extraTabs={[{
                        key: 'filters',
                        label: 'Filters',
                        content:
                            tableSource === 'inventory' ? (
                                inventory.filterContent
                            ) : (
                            <VStack align="stretch" spacing={4}>
                                {!locationId && (
                                    <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                                            LOCATIONS
                                        </Text>
                                        <LocationSelector
                                            selectedLocations={selectedLocations}
                                            onLocationChange={handleLocationChange}
                                            width="100%"
                                            icon={TbBuilding}
                                        />
                                    </Box>
                                )}
                                <Box>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                                        INSURANCE
                                    </Text>
                                    <FilterSelector
                                        label="All Insurance"
                                        placeholder="Select Insurance"
                                        options={insuranceOptionsForSelector}
                                        selectedValues={selectedInsurances}
                                        onChange={handleInsuranceChange}
                                        width="100%"
                                        icon={TbShieldCheck}
                                    />
                                </Box>
                                <Box>
                                    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                                        MAKE
                                    </Text>
                                    <FilterSelector
                                        label="All Makes"
                                        placeholder="Select Make"
                                        options={makeOptionsForSelector}
                                        selectedValues={selectedMakes}
                                        onChange={handleMakesChange}
                                        width="100%"
                                        icon={TbCar}
                                    />
                                </Box>
                                <ExcludeEvsToggle />
                                {viewMode === 'parts' && (
                                    <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                                            PART TYPE
                                        </Text>
                                        <FilterSelector
                                            label="All Part Types"
                                            placeholder="Select Part Type"
                                            options={filterOptions.partTypes}
                                            selectedValues={selectedPartTypes}
                                            onChange={handlePartTypesChange}
                                            width="100%"
                                            icon={TbPuzzle}
                                        />
                                    </Box>
                                )}
                                {viewMode === 'parts' && (
                                    <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={2}>
                                            VENDOR
                                        </Text>
                                        <FilterSelector
                                            label="All Vendors"
                                            placeholder="Select Vendor"
                                            options={vendorOptionsForSelector}
                                            selectedValues={selectedVendors}
                                            onChange={handleVendorsChange}
                                            width="100%"
                                            icon={TbTruck}
                                        />
                                    </Box>
                                )}
                            </VStack>
                            ),
                    }]}
                />
                <Tooltip label={isFullScreen ? 'Exit full screen' : 'Full screen table'} placement="top" hasArrow fontSize="xs">
                    <IconButton
                        aria-label={isFullScreen ? 'Exit full screen' : 'Full screen table'}
                        icon={<Icon as={(isFullScreen ? TbMinimize : TbMaximize) as React.ElementType} boxSize={4} />}
                        size="sm"
                        h={BTN_SIZE}
                        w={BTN_SIZE}
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.600"
                        borderRadius="md"
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.100' }}
                        onClick={() => setIsFullScreen((v) => !v)}
                    />
                </Tooltip>
                {tableSource === 'inventory' ? (
                    <Menu>
                        <Tooltip label="Export" placement="top" hasArrow fontSize="xs">
                            <MenuButton
                                as={IconButton}
                                aria-label="Export"
                                icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                                size="sm"
                                h={BTN_SIZE}
                                w={BTN_SIZE}
                                variant="outline"
                                borderColor="gray.300"
                                color="gray.600"
                                borderRadius="md"
                                _hover={{ bg: 'gray.50' }}
                                _active={{ bg: 'gray.100' }}
                                isLoading={inventory.isExporting}
                            />
                        </Tooltip>
                        <MenuList minW="140px" zIndex={20}>
                            <MenuItem
                                fontSize="sm"
                                onClick={() => inventory.handleExport('csv')}
                            >
                                Download CSV
                            </MenuItem>
                            <MenuItem
                                fontSize="sm"
                                onClick={() => inventory.handleExport('xlsx')}
                            >
                                Download Excel
                            </MenuItem>
                        </MenuList>
                    </Menu>
                ) : (
                    <Tooltip label="Export to CSV" placement="top" hasArrow fontSize="xs">
                        <IconButton
                            aria-label="Export to CSV"
                            icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                            size="sm"
                            h={BTN_SIZE}
                            w={BTN_SIZE}
                            variant="outline"
                            borderColor="gray.300"
                            color="gray.600"
                            borderRadius="md"
                            _hover={{ bg: 'gray.50' }}
                            _active={{ bg: 'gray.100' }}
                            onClick={handleExportCsv}
                            isLoading={isExporting}
                        />
                    </Tooltip>
                )}
            </HStack>
        </Flex>
    );

    return (
        <ReportingFullScreenShell
            isOpen={isFullScreen}
            onClose={() => setIsFullScreen(false)}
            fullscreenToolbar={isFullScreen ? auditingToolbarRow : undefined}
        >
            <VStack
                spacing={0}
                align="stretch"
                w="100%"
                h="100%"
                pt={0}
                flex={1}
                minW={0}
                minH={0}
                flexGrow={isFullScreen ? 1 : undefined}
                overflow={isFullScreen ? 'hidden' : undefined}
            >
                {!isFullScreen && auditingToolbarRow}

                <Box
                    w="100%"
                    flex={1}
                    minW={0}
                    minH={0}
                    overflowX="auto"
                    overflowY="auto"
                    bg="white"
                    {...(!isFullScreen && {
                        border: '1px solid',
                        borderColor: 'gray.200',
                        borderRadius: 'lg',
                    })}
                    sx={{
                        ...tableSettingsSx,
                        overscrollBehavior: 'contain',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                        '& table': {
                            width: 'max-content !important',
                            minWidth: '100%',
                        },
                        '& thead': {
                            zIndex: '3 !important',
                        },
                        '& th': {
                            ...(tableSettingsSx['& th'] || {}),
                            borderBottomWidth: '0 !important',
                            boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                            bg: 'gray.50',
                        },
                        '& th:first-of-type': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 5,
                            bg: 'gray.50',
                        },
                        '& td:first-of-type': {
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            bg: 'white',
                        },
                        '& tr:hover > td:first-of-type': {
                            bg: 'gray.50',
                        },
                    }}
                >
                    {tableSource === 'inventory' ? (
                        inventory.table
                    ) : isLoading ? (
                        <VStack spacing={2} p={4}>
                            {Array.from({ length: 10 }).map((_, i) => (
                                <Skeleton key={i} height="32px" width="100%" />
                            ))}
                        </VStack>
                    ) : viewMode === 'orders' ? (
                        <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} zIndex={1}>
                                <Tr>
                                    {visibleOrderCols.map((colKey) => (
                                        <Fragment key={colKey}>{renderOrdersHeaderCell(colKey)}</Fragment>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {orders.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={Math.max(visibleOrderCols.length, 1)} textAlign="center" py={8}>
                                            <Text color="gray.500" fontSize="sm">
                                                No orders found
                                            </Text>
                                        </Td>
                                    </Tr>
                                ) : (
                                    orders.map((order: AuditingOrderItem) => {
                                        const { isSaved, isFlagged } = getBookmarkTypes(bookmarks, order.orderId, 'order');
                                        return (
                                            <Tr key={order.orderId} _hover={{ bg: 'gray.50' }}>
                                                {visibleOrderCols.map((colKey) => (
                                                    <Fragment key={colKey}>
                                                        {renderOrdersBodyCell(colKey, order, { isSaved, isFlagged })}
                                                    </Fragment>
                                                ))}
                                            </Tr>
                                        );
                                    })
                                )}
                            </Tbody>
                        </Table>
                    ) : viewMode === 'parts' ? (
                        <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} zIndex={1}>
                                <Tr>
                                    {visiblePartCols.map((colKey) => (
                                        <Fragment key={colKey}>{renderPartsHeaderCell(colKey)}</Fragment>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {parts.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={Math.max(visiblePartCols.length, 1)} textAlign="center" py={8}>
                                            <Text color="gray.500" fontSize="sm">
                                                No parts found
                                            </Text>
                                        </Td>
                                    </Tr>
                                ) : (
                                    parts.map((part: AuditingPartItem) => {
                                        const { isSaved, isFlagged } = getBookmarkTypes(bookmarks, part.partId, 'part');
                                        return (
                                            <Tr key={part.partId} _hover={{ bg: 'gray.50' }}>
                                                {visiblePartCols.map((colKey) => (
                                                    <Fragment key={colKey}>
                                                        {renderPartsBodyCell(colKey, part, { isSaved, isFlagged })}
                                                    </Fragment>
                                                ))}
                                            </Tr>
                                        );
                                    })
                                )}
                            </Tbody>
                        </Table>
                    ) : (
                        /* Set Aside View */
                        <Table variant="simple" size="sm">
                            <Thead position="sticky" top={0} zIndex={1}>
                                <Tr>
                                    <Th {...thStyle}>Type</Th>
                                    <Th {...thStyle}>RO</Th>
                                    <Th {...thStyle}>Make</Th>
                                    <Th {...thStyle}>Insurance</Th>
                                    <Th {...thStyle}>Part Name</Th>
                                    <Th {...thStyle}>Status</Th>
                                    <Th {...thStyle}>Created</Th>
                                    <Th {...thStyle} textAlign="center" w="60px" aria-label="Row actions" />
                                </Tr>
                            </Thead>
                            <Tbody>
                                {bookmarks.length === 0 ? (
                                    <Tr>
                                        <Td colSpan={8} textAlign="center" py={8}>
                                            <VStack spacing={2}>
                                                <Text color="gray.500" fontSize="sm">No saved or flagged items yet</Text>
                                                <Text color="gray.400" fontSize="xs">Use the Actions menu on Orders or Parts to save items for later</Text>
                                            </VStack>
                                        </Td>
                                    </Tr>
                                ) : (
                                    bookmarks.map((bookmark) => (
                                        <Tr key={bookmark.id} _hover={{ bg: 'gray.50' }}>
                                            <Td {...tdStyle}>
                                                <Tag size="sm" colorScheme={bookmark.targetType === 'order' ? 'blue' : 'purple'} fontSize="xs">
                                                    {bookmark.targetType === 'order' ? 'Order' : 'Part'}
                                                </Tag>
                                            </Td>
                                            <Td {...tdStyle}>
                                                <HStack spacing={1}>
                                                    {bookmark.bookmarkType === 'saved' && <Icon as={TbBookmark as React.ElementType} boxSize={3} color="blue.500" />}
                                                    {bookmark.bookmarkType === 'flagged' && <Icon as={TbFlag as React.ElementType} boxSize={3} color="orange.500" />}
                                                    <Link
                                                        as={RouterLink}
                                                        to={`/order/${bookmark.orderId}`}
                                                        state={{ initialStatus: 'selection' }}
                                                        target="_blank"
                                                        color="#0071E3"
                                                        fontWeight="medium"
                                                        _hover={{ textDecoration: 'underline' }}
                                                    >
                                                        {bookmark.roNumber || '—'}
                                                    </Link>
                                                </HStack>
                                            </Td>
                                            <Td {...tdStyle}>
                                                {tableDetailMode ? (
                                                    <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                                        {bookmark.vehicleMake || '—'}
                                                    </Text>
                                                ) : (
                                                    <Text noOfLines={1} maxW="100px">
                                                        {bookmark.vehicleMake || '—'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td {...tdStyle}>
                                                {tableDetailMode ? (
                                                    <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                                        {bookmark.insuranceName || '—'}
                                                    </Text>
                                                ) : (
                                                    <Text noOfLines={1} maxW="120px">
                                                        {bookmark.insuranceName || '—'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td {...tdStyle}>
                                                {tableDetailMode ? (
                                                    <Text fontSize="xs" whiteSpace="normal" wordBreak="break-word">
                                                        {bookmark.partName || '—'}
                                                    </Text>
                                                ) : (
                                                    <Text noOfLines={1} maxW="180px" title={bookmark.partName || ''}>
                                                        {bookmark.partName ? truncateText(bookmark.partName) : '—'}
                                                    </Text>
                                                )}
                                            </Td>
                                            <Td {...tdStyle}>
                                                <Tag
                                                    size="sm"
                                                    colorScheme={bookmark.bookmarkType === 'saved' ? 'blue' : 'orange'}
                                                    fontSize="xs"
                                                >
                                                    {bookmark.bookmarkType === 'saved' ? 'Saved' : 'Flagged'}
                                                </Tag>
                                            </Td>
                                            <Td {...tdStyle}>
                                                {formatDate(bookmark.createdAt)}
                                            </Td>
                                            <Td {...tdStyle} textAlign="center">
                                                <Menu>
                                                    <MenuButton
                                                        as={IconButton}
                                                        icon={<Icon as={TbDotsVertical as React.ElementType} />}
                                                        variant="ghost"
                                                        size="xs"
                                                        aria-label="Actions"
                                                    />
                                                    <MenuList py={1} minW="160px">
                                                        <MenuItem
                                                            fontSize="sm"
                                                            color="red.500"
                                                            onClick={() => deleteBookmark.mutate({
                                                                targetId: bookmark.targetId,
                                                                targetType: bookmark.targetType,
                                                                bookmarkType: bookmark.bookmarkType
                                                            })}
                                                        >
                                                            <HStack spacing={2}>
                                                                <Icon as={TbBookmark as React.ElementType} boxSize={4} />
                                                                <Text>Remove</Text>
                                                            </HStack>
                                                        </MenuItem>
                                                    </MenuList>
                                                </Menu>
                                            </Td>
                                        </Tr>
                                    ))
                                )}
                            </Tbody>
                        </Table>
                    )}
                </Box>

                {/* Pagination - hide for Set Aside view */}
                {(tableSource === 'inventory' || viewMode !== 'setAside') && (
                    <Box flexShrink={0}>
                        <Pagination
                            currentPage={
                                tableSource === 'inventory' ? inventory.page : currentPage
                            }
                            totalPages={
                                tableSource === 'inventory' ? inventory.totalPages : totalPages
                            }
                            onPageChange={
                                tableSource === 'inventory'
                                    ? inventory.setPage
                                    : viewMode === 'orders'
                                      ? setOrdersPage
                                      : setPartsPage
                            }
                            isLoading={isLoading}
                            hasNextPage={
                                tableSource === 'inventory'
                                    ? false
                                    : viewMode === 'orders'
                                      ? ordersHasNextPage
                                      : false
                            }
                            size="sm"
                        />
                    </Box>
                )}

                {partOptionsModal && (
                    <PartOptionsModal
                        isOpen
                        onClose={() => setPartOptionsModal(null)}
                        orderPartId={partOptionsModal.orderPartId}
                        orderId={partOptionsModal.orderId}
                        source="order"
                    />
                )}
            </VStack>
        </ReportingFullScreenShell>
    );
};

export default AuditingTab;
